import type { DefaultMantineColor } from "@mantine/core";
import { Button, Radio } from "@mantine/core";
import { useMemo } from "react";
import { useAnalytics } from "@/analytics";
import styles from "./RadioCard.module.css";
import type { RadioCardProps } from "./types";

function hoverOverride(color: DefaultMantineColor | undefined) {
    return () => {
        return {
            root: {
                "--button-hover": color,
            },
        };
    };
}

export function RadioCard(props: RadioCardProps) {
    const { dispatcher } = useAnalytics();
    const { actionId, extraContext, eventData } = props.analytics;
    const enhancedDispatcher = extraContext ? dispatcher.withExtra(extraContext) : dispatcher;

    const overrideHoverVarsResolver = useMemo(() => {
        return hoverOverride(props.color);
    }, [props.color]);
    function onClick(event: React.MouseEvent<HTMLButtonElement>) {
        void enhancedDispatcher.dispatchAction("ButtonClick", actionId, { ...eventData });
        props.onClick?.(event);
    }
    const { iconColor, radioColor, ...rest } = props;
    const handleWrapperEnter = () => {
        void enhancedDispatcher.dispatchAction("KeypressEnter", actionId, eventData);
    };

    return (
        <Button
            {...rest}
            classNames={{ root: styles.RadioCardRoot }}
            onClick={onClick}
            onKeyUp={(e) => e.key === "Enter" && handleWrapperEnter}
            data-selected={props.selected}
            vars={overrideHoverVarsResolver}
        >
            <Radio
                classNames={{ label: styles.RadioCardLabel, body: styles.RadioCardBody }}
                disabled={props.disabled}
                checked={props.selected}
                readOnly
                color={radioColor}
                iconColor={iconColor}
                tabIndex={-1}
                value={props.value ?? props.label}
                label={props.label}
                {...props.radioProps}
            />
        </Button>
    );
}
